import { GRAVITY_G, type MapDef, type PlanetType } from "../sim";
import { type AsteroidSettings, CUSTOM_MAP_LIMITS } from "../sim/maps";
import { wrappedDelta } from "../sim/math/torus";

/**
 * Pure editing operations for the map editor (P11.1). Every function returns a new MapDef
 * (the editor keeps an undo-friendly, immutable history of them) and keeps objects inside
 * the world.
 */

export type EditKind = "planet" | "spawn" | "fuel";

export interface Selection {
	kind: EditKind;
	index: number;
}

/** Hit radius (world units) for the small objects: spawn points and fueling stations. */
export const POINT_HIT_RADIUS = 60;

function clone(map: MapDef): MapDef {
	return structuredClone(map);
}

function clampInto(v: number, size: number): number {
	return Math.min(size - 1, Math.max(0, Math.round(v)));
}

/** The topmost object at a world position: small points first, then planets (last drawn wins). */
export function hitTest(map: MapDef, x: number, y: number, slack = 0): Selection | null {
	const size = { width: map.size.w, height: map.size.h };
	const near = (p: { x: number; y: number }, r: number) => {
		const d = wrappedDelta(p, { x, y }, size);
		return Math.hypot(d.x, d.y) <= r + slack;
	};
	for (let i = map.spawnPoints.length - 1; i >= 0; i--) {
		if (near(map.spawnPoints[i] as { x: number; y: number }, POINT_HIT_RADIUS))
			return { kind: "spawn", index: i };
	}
	for (let i = map.fuelItems.length - 1; i >= 0; i--) {
		if (near(map.fuelItems[i] as { x: number; y: number }, POINT_HIT_RADIUS))
			return { kind: "fuel", index: i };
	}
	for (let i = map.planets.length - 1; i >= 0; i--) {
		const p = map.planets[i];
		if (p && near(p, p.radius)) return { kind: "planet", index: i };
	}
	return null;
}

function listOf(map: MapDef, kind: EditKind): Array<{ x: number; y: number }> {
	return kind === "planet" ? map.planets : kind === "spawn" ? map.spawnPoints : map.fuelItems;
}

/** Moves the selected object to a world position (clamped into the world). */
export function moveTo(map: MapDef, sel: Selection, x: number, y: number): MapDef {
	const next = clone(map);
	const item = listOf(next, sel.kind)[sel.index];
	if (item) {
		item.x = clampInto(x, map.size.w);
		item.y = clampInto(y, map.size.h);
	}
	return next;
}

/** Adds an object at a position; returns the new map and the selection of the new object. */
export function addObject(
	map: MapDef,
	kind: EditKind,
	x: number,
	y: number,
): { map: MapDef; selection: Selection } {
	const next = clone(map);
	const at = { x: clampInto(x, map.size.w), y: clampInto(y, map.size.h) };
	if (kind === "planet") next.planets.push({ type: "pluto", mass: 200, radius: 120, ...at });
	else if (kind === "spawn") next.spawnPoints.push(at);
	else next.fuelItems.push({ amount: 2, ...at });
	return { map: next, selection: { kind, index: listOf(next, kind).length - 1 } };
}

export function removeObject(map: MapDef, sel: Selection): MapDef {
	const next = clone(map);
	listOf(next, sel.kind).splice(sel.index, 1);
	return next;
}

/** Changes a planet's type, radius or mass (clamped to the custom-map limits). */
export function updatePlanet(
	map: MapDef,
	index: number,
	change: Partial<{ type: PlanetType; radius: number; mass: number }>,
): MapDef {
	const next = clone(map);
	const p = next.planets[index];
	if (!p) return next;
	const L = CUSTOM_MAP_LIMITS;
	if (change.type) p.type = change.type;
	if (change.radius !== undefined)
		p.radius = Math.round(Math.min(L.radiusMax, Math.max(L.radiusMin, change.radius)));
	if (change.mass !== undefined)
		p.mass = Math.round(Math.min(L.massMax, Math.max(L.massMin, change.mass)));
	return next;
}

export function updateFuel(map: MapDef, index: number, amount: number): MapDef {
	const next = clone(map);
	const f = next.fuelItems[index];
	const L = CUSTOM_MAP_LIMITS;
	if (f) f.amount = Math.round(Math.min(L.fuelRateMax, Math.max(L.fuelRateMin, amount)));
	return next;
}

/**
 * Sets one asteroid field value (P11.12), clamped to the limits; undefined = back to the
 * original value. A map with no values left drops the `asteroids` block.
 */
export function updateAsteroids(
	map: MapDef,
	key: keyof AsteroidSettings,
	value: number | undefined,
): MapDef {
	const L = CUSTOM_MAP_LIMITS;
	const next = clone(map);
	const a: AsteroidSettings = { ...next.asteroids };
	if (value === undefined || !Number.isFinite(value)) delete a[key];
	else if (key === "max") a.max = Math.round(Math.min(L.asteroidsMax, Math.max(0, value)));
	else if (key === "perMinute")
		a.perMinute = Math.round(Math.min(L.asteroidsPerMinuteMax, Math.max(0, value)) * 10) / 10;
	else
		a.speed =
			Math.round(Math.min(L.asteroidSpeedMax, Math.max(L.asteroidSpeedMin, value)) * 10) / 10;
	if (Object.keys(a).length > 0) next.asteroids = a;
	else delete next.asteroids;
	return next;
}

/** Resizes the world (clamped to the limits); objects keep their relative positions. */
export function resize(map: MapDef, w: number, h: number): MapDef {
	const L = CUSTOM_MAP_LIMITS;
	const nw = Math.round(Math.min(L.worldMax, Math.max(L.worldMin, w)));
	const nh = Math.round(Math.min(L.worldMax, Math.max(L.worldMin, h)));
	const sx = nw / map.size.w;
	const sy = nh / map.size.h;
	const next = clone(map);
	next.size = { w: nw, h: nh };
	for (const list of [next.planets, next.spawnPoints, next.fuelItems] as Array<
		Array<{ x: number; y: number }>
	>) {
		for (const o of list) {
			o.x = clampInto(o.x * sx, nw);
			o.y = clampInto(o.y * sy, nh);
		}
	}
	return next;
}

/**
 * Planet pull at a position in world units per tick² (the sim's `G·M/d²`, measured across the
 * wrap seam), for the editor's gravity overlay. Inside a planet it is zero.
 */
export function gravityAt(map: MapDef, x: number, y: number): { x: number; y: number } {
	const size = { width: map.size.w, height: map.size.h };
	let ax = 0;
	let ay = 0;
	for (const p of map.planets) {
		const d = wrappedDelta({ x, y }, p, size);
		const dist = Math.hypot(d.x, d.y);
		if (dist <= p.radius) continue;
		const a = (GRAVITY_G * p.mass) / (dist * dist);
		ax += (d.x / dist) * a;
		ay += (d.y / dist) * a;
	}
	return { x: ax, y: ay };
}
